import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  // Transitions widened to `all` so the Aurora gradient + shadow shifts on
  // hover/active feel smooth, not just the color crossfade the old
  // `transition-colors` shorthand allowed.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Brand primary CTA. Aurora indigo with a soft brand-glow on hover —
        // the glow ring is the same token used on focus, so hover and focus
        // visually agree (no awkward jump when the mouse lands then keyboard
        // takes over).
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/92 hover:shadow-[var(--shadow-brand-glow)] active:bg-primary/95 active:shadow-sm',
        // Live-data accent — for live-data CTAs (Acknowledge, Subscribe, Watch
        // Live). Variant key kept as `phosphor` (public API); colors use the
        // canonical `accent-live` tokens.
        phosphor:
          'bg-accent-live text-accent-live-foreground hover:bg-accent-live/90 active:bg-accent-live/95',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/95',
        outline:
          'border border-input bg-surface-raised hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // Heights/padding are driven by the density control tokens
        // (--control-height-* / --control-padding-x-*) so all four density
        // modes (comfortable/compact/touch/wall) resize every button from the
        // one --space-base knob. At `comfortable`, md=36px / lg=44px / sm=28px
        // — `default` maps to `md`, `lg` keeps the WCAG 2.5.5 (AA) 44px target.
        default:
          'h-[var(--control-height-md)] px-[var(--control-padding-x-md)] py-2',
        // `sm` is intentionally smaller — DO NOT use as the primary CTA on a
        // touch device; pair with a larger hit-area wrapper or keep desktop-only.
        sm: 'h-[var(--control-height-sm)] rounded-lg px-[var(--control-padding-x-sm)] text-xs',
        lg: 'h-[var(--control-height-lg)] rounded-xl px-[var(--control-padding-x-lg)] text-base',
        // `icon-xs` is for inline clear/affordance glyphs (combobox clear,
        // chip dismiss) — below the touch floor by design; never a primary action.
        'icon-xs': 'h-6 w-6 rounded-md [&_svg]:size-3',
        // `icon` matches the default (md) control height, kept square.
        icon: 'h-[var(--control-height-md)] w-[var(--control-height-md)]',
        // `icon-sm` is the desktop-dense variant — NOT WCAG-compliant for
        // the primary action on touch devices; reserve for toolbars where
        // the surrounding row provides the hit area.
        'icon-sm': 'h-[var(--control-height-sm)] w-[var(--control-height-sm)]',
        'icon-lg': 'h-[var(--control-height-lg)] w-[var(--control-height-lg)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // WCAG 4.1.2 — when the underlying element is a non-button (asChild=true)
    // the consumer may render an <a>/<span>/etc. that doesn't carry the
    // implicit `disabled` semantics. Mirror `disabled` to `aria-disabled`
    // so AT announcement stays consistent across both render paths.
    const ariaDisabled =
      // Don't override explicit consumer-supplied aria-disabled.
      props['aria-disabled'] ??
      (disabled ? true : undefined);
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled}
        aria-disabled={ariaDisabled}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
